import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastComponent, ToastData, ToastKind } from '../components/toast/toast.component';

export interface OpenToastOptions {
  description?: string;
  kind?: ToastKind;
  /** Auto-dismiss in ms. Defaults: success/info 5s, error/warning 8s. */
  duration?: number;
}

/**
 * Toast + snackbar helper. All UI-facing feedback goes through here so
 * we have one place to tune size, duration, position, and styling.
 *
 * The two legacy methods (openSnackBar, openSnackBarError) remain
 * signature-compatible with everywhere they're already called — the
 * second argument (`action`) is accepted and ignored (was mostly the
 * literal 'x' in the codebase) and an optional third argument adds a
 * description line under the title.
 *
 * New callers should use `openToast(title, { description, kind })`.
 */
@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  constructor(private _snackBar: MatSnackBar) {}

  /**
   * DEBUG MODE — set to true to keep every toast visible until the user
   * dismisses it manually via the ✕ button. Useful while iterating on
   * toast copy and layout. Flip back to false before shipping so toasts
   * auto-dismiss on their normal 5s / 8s schedule.
   */
  private static readonly DEBUG_KEEP_OPEN = true;

  openToast(title: string, options?: OpenToastOptions): void {
    const kind: ToastKind = options?.kind ?? 'success';

    // duration: 0 in Angular Material means "never auto-dismiss".
    const duration = SnackBarService.DEBUG_KEEP_OPEN
      ? 0
      : options?.duration ??
        (kind === 'error' || kind === 'warning' ? 8000 : 5000);

    const data: ToastData = {
      title,
      description: options?.description,
      kind,
    };

    this._snackBar.openFromComponent(ToastComponent, {
      data,
      duration,
      // Centered on all screen sizes — 'right' plus a wide toast body
      // could push the container off-screen on narrow phones.
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['appToast', `appToast--${kind}`],
    });
  }

  // ── Legacy API — kept for backwards compatibility across the app.

  openSnackBar(title: string, _action?: string, description?: string): void {
    this.openToast(title, { kind: 'success', description });
  }

  openSnackBarError(title: string, _action?: string, description?: string): void {
    this.openToast(title, { kind: 'error', description });
  }
}
